import { ReactElement, ReactNode, useState } from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { colors } from '~/styles/colors';
import { PRIMARY_CONDENSER_OPTIONS, STAGE_DELAY_MAX, STAGE_DELAY_MIN } from '../constants';
import {
  FieldLabel, HelperText, mobileQuery, NativeSelect, SelectWrapper,
} from '../ds';
import {
  validateOptionalNumber, validatePositiveNumber, validateStageDelay,
} from '../helpers';
import { DamEcoSimplifiedFormValues } from '../types';
import { ChevronDownIcon, GearIcon } from './icons';
import { StepperField } from './StepperField';

const STROKE = colors.stroke['soft-200'];

const Box = styled.div`
  margin-top: 22px;
  overflow: hidden;
  border: 1px solid ${STROKE};
  border-radius: 10px;
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  border: none;
  background: ${colors.celsius['surface-subtle']};
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  ${mobileQuery} {
    flex-wrap: wrap;
    gap: 10px;
    padding: 14px 16px;
  }
`;

const HeaderTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text['main-900']};
`;

const HeaderSubtitle = styled.span`
  font-size: 13px;
  color: ${colors.text['sub-350']};

  ${mobileQuery} {
    order: 4;
    flex-basis: 100%;
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: flex;
  margin-left: auto;
  transition: transform 0.3s;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const Body = styled.div`
  padding: 24px 20px;
  border-top: 1px solid ${STROKE};
  background: ${colors.bg['white-0']};

  ${mobileQuery} {
    padding: 18px 16px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  align-items: start;

  & + & {
    margin-top: 24px;
  }

  ${mobileQuery} {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;

    & + & {
      margin-top: 18px;
    }
  }
`;

const CondenserLabel = styled(FieldLabel)`
  align-items: flex-start;
  min-height: 32px;

  ${mobileQuery} {
    min-height: 0;
  }
`;

const ADVANCED_FIELDS: (keyof DamEcoSimplifiedFormValues)[] = ['upperHysteresis', 'lowerHysteresis', 'lti', 'stageAddDelay', 'stageReduceDelay'];

interface AdvancedOptionsProps {
  readOnly: boolean;
  /** Ilustrações do design, abaixo dos campos. */
  illustrations?: ReactNode;
}

export function AdvancedOptions({ readOnly, illustrations }: Readonly<AdvancedOptionsProps>): ReactElement {
  const { t } = useTranslation();
  const { control } = useFormContext<DamEcoSimplifiedFormValues>();
  const { errors } = useFormState({ control });
  const [open, setOpen] = useState(false);

  // Um erro de validação aqui abre o painel, para o usuário ver o campo a corrigir.
  const isOpen = open || ADVANCED_FIELDS.some((field) => !!errors[field]);
  const minutes = t('ecoSimplificadoMin');
  const delayRange = t('ecoSimplificadoFaixaAtraso', { min: STAGE_DELAY_MIN, max: STAGE_DELAY_MAX });

  return (
    <Box>
      <Header type="button" onClick={() => setOpen((prev) => !prev)} aria-expanded={isOpen} aria-controls="eco-simplificado-opcoes-avancadas">
        <GearIcon color={colors.brand.primary.base} strokeWidth={1.8} />
        <HeaderTitle>{t('ecoSimplificadoOpcoesAvancadas')}</HeaderTitle>
        <HeaderSubtitle>{t('ecoSimplificadoHistereseIntervaloComando')}</HeaderSubtitle>
        <Chevron $open={isOpen}><ChevronDownIcon size={20} color={colors.text['sub-350']} /></Chevron>
      </Header>
      <Body id="eco-simplificado-opcoes-avancadas" hidden={!isOpen}>
        <Grid>
          <StepperField
            name="upperHysteresis"
            label={t('histereseSuperior')}
            unit="°C"
            limits={{ min: 0, step: 1 }}
            validate={validatePositiveNumber}
            disabled={readOnly}
            extra={{ hint: t('ecoSimplificadoHistereseSuperiorDica'), alignRows: true }}
          />
          <StepperField
            name="lowerHysteresis"
            label={t('histereseInferior')}
            unit="°C"
            limits={{ min: 0, step: 1 }}
            validate={validatePositiveNumber}
            disabled={readOnly}
            extra={{ hint: t('ecoSimplificadoHistereseInferiorDica'), alignRows: true }}
          />
          <StepperField
            name="lti"
            label={t('ecoSimplificadoLti')}
            unit="°C"
            validate={validateOptionalNumber}
            disabled={readOnly}
            extra={{ hint: <Trans i18nKey="ecoSimplificadoLtiDica" components={{ b: <b /> }} />, alignRows: true }}
          />
        </Grid>
        <Grid>
          <StepperField
            name="stageAddDelay"
            label={t('ecoSimplificadoAtrasoAdicionarEstagio')}
            unit={minutes}
            limits={{ min: STAGE_DELAY_MIN, max: STAGE_DELAY_MAX }}
            validate={validateStageDelay}
            disabled={readOnly}
            extra={{ hint: t('ecoSimplificadoAtrasoAdicionarDica', { faixa: delayRange }), alignRows: true }}
          />
          <StepperField
            name="stageReduceDelay"
            label={t('ecoSimplificadoAtrasoReduzirEstagio')}
            unit={minutes}
            limits={{ min: STAGE_DELAY_MIN, max: STAGE_DELAY_MAX }}
            validate={validateStageDelay}
            disabled={readOnly}
            extra={{ hint: t('ecoSimplificadoAtrasoReduzirDica', { faixa: delayRange }), alignRows: true }}
          />
          <div>
            <CondenserLabel as="label" htmlFor="eco-simplificado-condensadora">{t('ecoSimplificadoCondensadoraPrimaria')}</CondenserLabel>
            <Controller
              control={control}
              name="primaryCondenser"
              render={({ field }) => (
                <SelectWrapper>
                  <NativeSelect
                    id="eco-simplificado-condensadora"
                    data-testid="eco-simplificado-condensadora"
                    $bold
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value)}
                    disabled={readOnly}
                  >
                    {PRIMARY_CONDENSER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                    ))}
                  </NativeSelect>
                  <ChevronDownIcon color={colors.text['sub-350']} />
                </SelectWrapper>
              )}
            />
            <HelperText>{t('ecoSimplificadoCondensadoraPrimariaDica')}</HelperText>
          </div>
        </Grid>
        {illustrations}
      </Body>
    </Box>
  );
}
